import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { kiemPhienNgay } from '../lib/auth/oidc';
import { suKienRepository } from '../lib/repositories/su-kien.repository';
import type { SuKienNguoiDung, TrangThaiKenh } from '../lib/repositories/su-kien.repository';
import { lamMoiNguCanhCuaToi } from '../modules/auth/hooks/useNguCanhCuaToi';
import { lamMoiChinhSachBaoMat } from '../modules/bao-mat/hooks/useBaoMat';
import { lamMoiPhienDangNhap } from '../modules/tai-khoan/hooks/useTaiKhoan';
import { useAuthStore } from '../stores/auth';
import { useKenhSuKienStore } from '../stores/kenh-su-kien';
import { usePhamVi } from './usePhamVi';

/**
 * Mở kênh sự kiện của người đang đăng nhập và nối từng sự kiện vào cache của
 * TanStack Query. Gắn **một lần** trong khung đã đăng nhập (`AuthGate`).
 *
 * Effect ở đây là đồng bộ với một hệ bên ngoài (luồng mạng sống suốt phiên),
 * đúng chỗ dùng được `useEffect`: mở khi có người dùng, đóng khi đăng xuất hay
 * đổi người. Đóng kênh cũng là cách dừng hẳn khi `oidc.ts` kết thúc phiên —
 * nó xoá người dùng khỏi kho, `AuthGate` gỡ hook này.
 *
 * Chỉ người dùng của tổ chức mới có kênh (ADR-0036): user-service trả 403 cho
 * tài khoản nền tảng vì họ không thuộc tenant nào. Mở kênh cho họ thì chỉ thử
 * lại mãi và nhãn trạng thái báo "mất kết nối" sai.
 */
export function useKenhSuKien(): void {
  const userId = useAuthStore((s) => s.user?.id);
  const coKenh = usePhamVi() === 'tenant';
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId || !coKenh) return;
    const datTrangThai = (trangThai: TrangThaiKenh | 'tat') => useKenhSuKienStore.setState({ trangThai });

    // Lần `ready` thứ hai trở đi là nối lại sau khi đứt: sự kiện trong khoảng
    // đứt đã mất, nên làm mới mọi thứ kênh này lẽ ra đã báo.
    let daSanSang = false;

    const xuLy = (suKien: SuKienNguoiDung) => {
      switch (suKien.type) {
        case 'ready':
          if (daSanSang) {
            lamMoiPhienDangNhap(queryClient);
            lamMoiNguCanhCuaToi(queryClient);
            lamMoiChinhSachBaoMat(queryClient);
            kiemPhienNgay();
          }
          daSanSang = true;
          break;
        case 'session.changed':
          lamMoiPhienDangNhap(queryClient);
          kiemPhienNgay();
          break;
        case 'session.locked':
          kiemPhienNgay();
          break;
        case 'tenant.changed':
          lamMoiNguCanhCuaToi(queryClient);
          if (suKien.data.reason === 'security') lamMoiChinhSachBaoMat(queryClient);
          break;
        case 'notification':
          // TODO(thông báo): chưa có màn thông báo trong cổng. Khi có, nối vào
          // đây — làm mới danh sách thông báo hoặc hiện toast theo `level`.
          // Hiện cố ý không hiện gì.
          break;
      }
    };

    const dong = suKienRepository.theoDoi({
      onSuKien: xuLy,
      onTrangThai: datTrangThai,
      onHetPhien: kiemPhienNgay,
    });
    if (!dong) return;

    return () => {
      dong();
      datTrangThai('tat');
    };
  }, [userId, coKenh, queryClient]);
}

/** Trạng thái kênh, cho nhãn "cập nhật trực tiếp" ở các màn. */
export function useTrangThaiKenhSuKien() {
  return useKenhSuKienStore((s) => s.trangThai);
}
